import { z } from 'zod/v4'

import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { planFeatureSchema } from './tenant-detail.screen.js'

/*
 * «Planes» (`admin.pricingPlans`): the subscription plans, in the order the route sends them. The Stripe price id and
 * the creation instant are not here: the page paints neither. Strict: a field that is not named here does not travel.
 */

export const adminPricingPlanRowSchema = z.strictObject({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  isActive: z.boolean(),
  isPublic: z.boolean(),
  limits: z.strictObject({
    maxTransactions: z.number(),
    maxImports: z.number(),
    maxUsers: z.number(),
    storageGb: z.number(),
    apiCallsPerMonth: z.number(),
  }),
  pricing: z.strictObject({
    monthly: z.number(),
    annual: z.number(),
    currency: z.string(),
  }),
  features: z.array(planFeatureSchema),
  /** Organisations on this plan. */
  tenantCount: z.number().int().min(0),
  updatedAt: z.iso.datetime(),
})

export const adminPricingPlansScreen = defineScreen({
  id: 'admin.pricingPlans',
  path: '/api/screens/admin/pricing-plans',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    plans: screenSection(z.array(adminPricingPlanRowSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.pricingPlans'],
  budget: { requests: 1, statements: 8, bytes: 40_000, p95Ms: 200 },
  summary: 'Pricing plans: limits, prices, features and how many organisations use each',
})

export type AdminPricingPlanRow = z.infer<typeof adminPricingPlanRowSchema>
export type AdminPricingPlansData = ScreenData<typeof adminPricingPlansScreen>
